import {
  CommentResp,
  ErrorResponse,
  isErrorResponse,
  SuccessResponse,
} from "@types"
import customFetch from "./helper"
import { commentAddedTag } from "../actions/revalidateTagParams"
import { revalidateTag } from "../actions/revalidateTag"

// Used for fetching comments for the new app directory comment system.
const API_V2 = "/comments/v2"

/**
 * Fetch Farcaster casts associated to a given URL.
 *
 * Cached for  1 hour since, depending on the URL,
 * this may take a bit of time and return a lot of data.
 *
 * @param url - The URL of the post to fetch casts for.
 * @returns The casts for the URL.
 */
export async function getCasts(url: string): Promise<CommentResp[]> {
  const response = await customFetch<CommentResp[]>(
    `${API_V2}/farcaster/url?url=${encodeURIComponent(url)}`,
    {
      next: {
        revalidate: 600,
      },
    },
    {
      bypassCookies: true,
    }
  )

  return response
}

/**
 * Fetch Farcaster casts and replies associated to a given URL.
 *
 * Cached for 10 minutes since, depending on the URL,
 * this may take a bit of time and return a lot of data.
 *
 * @param postId - The ID of the post to fetch casts and replies for.
 * @returns The casts and replies for the post.
 */
export async function getCastsAndReplies(
  postId: string
): Promise<CommentResp[]> {
  const response = await customFetch<CommentResp[]>(
    `${API_V2}/farcaster/${postId}`,
    {
      next: {
        revalidate: 600,
      },
    },
    {
      bypassCookies: true,
    }
  )

  return response
}

/**
 * Fetch all comments for a given post.
 *
 * If there are none, it returns an empty array.
 *
 * @param postId - The ID of the post to fetch comments for.
 * @returns The comments for the post, or an error response if something went wrong.
 */
export async function getAllComments(
  postId: string
): Promise<CommentResp[] | ErrorResponse> {
  const res = await customFetch<CommentResp[] | ErrorResponse>(
    `${API_V2}/${postId}`,
    {
      cache: "force-cache",
      next: {
        // Refresh every 30 minutes, or until a new comment is added.
        tags: [commentAddedTag(postId)],
      },
    },
    { ignoreImpersonation: true }
  )

  if (isErrorResponse(res)) {
    console.error("Error fetching comments", res)

    return res
  }

  return res
}

/**
 * Add a new comment to a post.
 *
 * If replyToCommentId is provided, the comment will be a reply to that comment.
 *
 * @param postId - The ID of the post to add the comment to.
 * @param text - The text of the comment.
 * @param replyToCommentId - The ID of the comment to reply to.
 * @returns The newly created comment, or an error response if something went wrong.
 */
export async function addComment(
  postId: string,
  text: string,
  replyToCommentId?: string
): Promise<SuccessResponse | ErrorResponse> {
  const res = await customFetch<SuccessResponse | ErrorResponse>(
    `${API_V2}/${postId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, replyToCommentId }),
    }
  )

  if (isErrorResponse(res)) {
    console.error("Error adding comment", res)

    return res
  }

  revalidateTag(commentAddedTag(postId))

  return res
}

/**
 * Update a comment.
 *
 * @param commentId - The ID of the comment to update.
 * @param text - The new text of the comment.
 * @returns The updated comment, or an error response if the comment was not found or something else went wrong.
 */
export async function updateComment(
  commentId: string,
  text: string
): Promise<SuccessResponse | ErrorResponse> {
  const res = await customFetch<SuccessResponse | ErrorResponse>(
    `${API_V2}/${commentId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text }),
    }
  )

  if (isErrorResponse(res)) {
    console.error("Error updating comment", res)

    return res
  }

  return res
}

/**
 * Delete a comment.
 *
 * @param commentId - The ID of the comment to delete.
 * @returns Null for success, or an error response if something went wrong.
 */
export async function deleteComment(
  commentId: string
): Promise<null | ErrorResponse> {
  const res = await customFetch<null | ErrorResponse>(
    `${API_V2}/${commentId}`,
    {
      method: "DELETE",
    }
  )

  if (isErrorResponse(res)) {
    console.error("Error deleting comment", res)

    return res
  }

  return res
}
